/**
 * ProjectManagerSheet (FE-3 §6.3)
 *
 * Pannello laterale per gestire i progetti:
 *  - tab/segmento Attivi e Archiviati;
 *  - ricerca testuale;
 *  - righe con icona/colore, nome, task_count, stato e azioni
 *    modifica/archivia/ripristina;
 *  - conferma prima di archiviare, con messaggio che i task NON saranno eliminati;
 *  - dopo archiviazione del progetto corrente, il caller (TaskPageNew) deve
 *    scegliere il primo attivo o inbox: lo notifichiamo via onArchiveCurrentScope.
 *
 * Niente elimina/duplica/riordina/preferiti nella prima release.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Archive,
  ArchiveRestore,
  Folder,
  Pencil,
  Search,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../ui/sheet";
import { Input } from "../../ui/input";
import { toast } from "sonner";
import type { TaskProject } from "../../../types/tasks";
import {
  archiveTaskProject,
  restoreTaskProject,
} from "../../../services/taskProjects";
import { describeProjectError } from "./ProjectForm";

type Tab = "active" | "archived";

export interface ProjectManagerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Progetti attivi (no archiviati). */
  activeProjects: TaskProject[];
  /** Progetti archiviati. */
  archivedProjects: TaskProject[];
  /** Lista globale in caricamento? */
  loading?: boolean;
  /** Scope corrente: per avvisare il caller se archivia il progetto corrente. */
  currentProjectId?: number | null;
  /** Apri form di modifica per il progetto. */
  onEditProject: (project: TaskProject) => void;
  /** Notifica che il progetto corrente è stato archiviato: il caller deve
   *  selezionare un nuovo scope (primo attivo o inbox). */
  onCurrentProjectArchived: (archivedId: number) => void;
  /** Callback dopo ogni mutation per ricaricare la lista (refetch). */
  onProjectsChanged: () => void | Promise<void>;
}

export default function ProjectManagerSheet({
  open,
  onOpenChange,
  activeProjects,
  archivedProjects,
  loading = false,
  currentProjectId,
  onEditProject,
  onCurrentProjectArchived,
  onProjectsChanged,
}: ProjectManagerSheetProps) {
  const [tab, setTab] = useState<Tab>("active");
  const [search, setSearch] = useState("");
  const [confirmArchive, setConfirmArchive] = useState<TaskProject | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<TaskProject | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  // Reset search quando si cambia tab o si chiude.
  useEffect(() => {
    if (open) setSearch("");
  }, [open, tab]);

  const list = tab === "active" ? activeProjects : archivedProjects;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q)
    );
  }, [list, search]);

  const handleArchive = async (project: TaskProject) => {
    setBusy(project.id);
    try {
      await archiveTaskProject(project.id);
      toast.success(`Progetto "${project.name}" archiviato`);
      await onProjectsChanged();
      if (currentProjectId === project.id) {
        onCurrentProjectArchived(project.id);
      }
    } catch (err) {
      toast.error(describeProjectError(err));
    } finally {
      setBusy(null);
      setConfirmArchive(null);
    }
  };

  const handleRestore = async (project: TaskProject) => {
    setBusy(project.id);
    try {
      await restoreTaskProject(project.id);
      toast.success(`Progetto "${project.name}" ripristinato`);
      await onProjectsChanged();
    } catch (err) {
      toast.error(describeProjectError(err));
    } finally {
      setBusy(null);
      setConfirmRestore(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[420px] h-[100dvh] p-0 bg-[var(--hexa-modal-bg)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)]"
      >
        <SheetHeader className="border-b border-[var(--hexa-border-subtle)] px-4 py-3">
          <SheetTitle className="text-base">Gestisci progetti</SheetTitle>
          <SheetDescription className="text-xs">
            Crea, modifica, archivia e ripristina i progetti del tenant.
          </SheetDescription>
        </SheetHeader>

        {/* Tabs */}
        <div className="flex items-center gap-1 p-3 border-b border-[var(--hexa-border-subtle)]">
          {(
            [
              { v: "active", label: `Attivi (${activeProjects.length})` },
              { v: "archived", label: `Archiviati (${archivedProjects.length})` },
            ] as Array<{ v: Tab; label: string }>
          ).map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setTab(opt.v)}
              className={`flex-1 h-8 text-xs font-medium rounded-md border transition ${
                tab === opt.v
                  ? "border-[var(--hexa-purple)] text-[var(--hexa-purple)] bg-[var(--hexa-purple-glow)]"
                  : "border-[rgba(106,77,255,0.25)] text-[var(--hexa-text-secondary)] hover:border-[var(--hexa-purple)]"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="p-3 border-b border-[var(--hexa-border-subtle)]">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
            <Input
              placeholder={`Cerca in ${tab === "active" ? "attivi" : "archiviati"}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 pr-2 bg-[var(--hexa-card-bg)] border-[rgba(106,77,255,0.25)] text-sm rounded-md"
            />
          </div>
        </div>

        {/* Lista */}
        <div className="flex-1 overflow-y-auto scrollbar-hexa">
          {loading && (
            <div className="px-4 py-6 text-xs text-center text-[var(--hexa-text-muted)]">
              Caricamento...
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="px-4 py-8 text-center">
              <Folder className="h-8 w-8 mx-auto text-[var(--hexa-text-muted)] opacity-50 mb-2" />
              <p className="text-sm text-[var(--hexa-text-secondary)]">
                {tab === "active"
                  ? "Nessun progetto attivo."
                  : "Nessun progetto archiviato."}
              </p>
              {search.trim() && (
                <p className="text-xs text-[var(--hexa-text-muted)] mt-1">
                  Prova a modificare la ricerca.
                </p>
              )}
            </div>
          )}

          {filtered.map((p) => (
            <ProjectRow
              key={p.id}
              project={p}
              isCurrent={currentProjectId === p.id}
              busy={busy === p.id}
              onEdit={() => {
                onEditProject(p);
                onOpenChange(false);
              }}
              onArchive={() => setConfirmArchive(p)}
              onRestore={() => setConfirmRestore(p)}
            />
          ))}
        </div>

        {/* Confirm archive */}
        <AlertDialog
          open={confirmArchive !== null}
          onOpenChange={(o) => !o && setConfirmArchive(null)}
        >
          <AlertDialogContent className="bg-[var(--hexa-modal-bg)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)]">
            <AlertDialogHeader>
              <AlertDialogTitle>Archivia progetto</AlertDialogTitle>
              <AlertDialogDescription>
                Stai per archiviare <strong>{confirmArchive?.name}</strong>.
                I task non verranno eliminati e resteranno associati al progetto.
                Potrai ripristinarlo in qualsiasi momento da "Archiviati".
                {currentProjectId === confirmArchive?.id && (
                  <>
                    {" "}
                    Verrai spostato automaticamente su un altro progetto attivo.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="h-8">Annulla</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => confirmArchive && handleArchive(confirmArchive)}
                className="h-8 bg-[#f59e0b] hover:bg-[#d97706] text-white"
              >
                Archivia
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirm restore */}
        <AlertDialog
          open={confirmRestore !== null}
          onOpenChange={(o) => !o && setConfirmRestore(null)}
        >
          <AlertDialogContent className="bg-[var(--hexa-modal-bg)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)]">
            <AlertDialogHeader>
              <AlertDialogTitle>Ripristina progetto</AlertDialogTitle>
              <AlertDialogDescription>
                Ripristinare <strong>{confirmRestore?.name}</strong>? Sarà di
                nuovo possibile crearci task e modificarne le colonne.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="h-8">Annulla</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => confirmRestore && handleRestore(confirmRestore)}
                className="h-8 bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white"
              >
                Ripristina
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}

interface ProjectRowProps {
  project: TaskProject;
  isCurrent: boolean;
  busy: boolean;
  onEdit: () => void;
  onArchive: () => void;
  onRestore: () => void;
}

function ProjectRow({
  project,
  isCurrent,
  busy,
  onEdit,
  onArchive,
  onRestore,
}: ProjectRowProps) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[var(--hexa-border-subtle)] hover:bg-[var(--hexa-surface-hover)] transition">
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded-md"
        style={{
          backgroundColor: `${project.color}22`,
          color: project.color,
        }}
      >
        <Folder className="h-3.5 w-3.5" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium truncate text-[var(--hexa-text-primary)]">
            {project.name}
          </span>
          {isCurrent && (
            <span className="inline-flex items-center px-1.5 h-4 rounded text-[10px] font-medium bg-[var(--hexa-purple-glow)] text-[var(--hexa-purple)]">
              Corrente
            </span>
          )}
          {project.is_archived && (
            <span className="inline-flex items-center px-1.5 h-4 rounded text-[10px] font-medium bg-[rgba(245,158,11,0.15)] text-[#f59e0b]">
              Archiviato
            </span>
          )}
        </div>
        <div className="text-[11px] text-[var(--hexa-text-muted)]">
          {project.task_count} task
          {project.description ? ` · ${project.description.slice(0, 60)}` : ""}
        </div>
      </div>
      <div className="flex items-center gap-0.5">
        {!project.is_archived && (
          <button
            type="button"
            onClick={onEdit}
            disabled={busy}
            title="Modifica"
            className="p-1.5 rounded text-[var(--hexa-text-muted)] hover:text-[var(--hexa-purple)] hover:bg-[var(--hexa-surface)] transition disabled:opacity-50"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        {!project.is_archived ? (
          <button
            type="button"
            onClick={onArchive}
            disabled={busy}
            title="Archivia"
            className="p-1.5 rounded text-[var(--hexa-text-muted)] hover:text-[#f59e0b] hover:bg-[var(--hexa-surface)] transition disabled:opacity-50"
          >
            <Archive className="h-3.5 w-3.5" />
          </button>
        ) : (
          <button
            type="button"
            onClick={onRestore}
            disabled={busy}
            title="Ripristina"
            className="p-1.5 rounded text-[var(--hexa-text-muted)] hover:text-[var(--hexa-purple)] hover:bg-[var(--hexa-surface)] transition disabled:opacity-50"
          >
            <ArchiveRestore className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
